import { useState } from 'react';
import { amendMatchDecision, matchMemberForApproval, newClientRequestId, replyActionsToApproval, reviewEvent } from '@lib/care-api';
import { useClient, useFeatures } from '@lib/clients';
import { isReceiveOnly, RECEIVE_ONLY_WORDING } from '@lib/receive-only';
import { inPresses, opFits } from '@lib/reply-groups';
import { STOP_WAITS_WORDING } from '@lib/staged-presses';
import { KIT_KINDS, MAX_REASON, quickKey } from '@lib/quick-keys';
import { fmtTs, type EventRow, type ThreadRow } from '@lib/care-model';
import { eventMainLine } from '@components/QueueTable';
import { useLeaveGuard } from '@lib/leave-guard';

/**
 * THE INBOUND QUEUE'S BUTTONS ON TOP (William, 16 Oct 2026, verbatim): "Like the print - put a checkmark
 * next to each name. Then on the top put the following buttons (which would apply to each checkmark: (a)
 * Opt-In; (b) Opt-Out; (c) Stop; (d) Send results; and (e) Send replacement kit. When the user checks the
 * boxes and then choosese from teh top of the page from the quick keys, then all of the items checked will
 * go to the approval lane (another tab) for the action the keys say."
 *
 * Every press here is QUEUED for Final Review (`toApproval: true`), whatever the deployment's staging
 * switch says; nothing takes effect until a reviewer approves the case. The server runs every refusal
 * first and queues nothing the approval could not carry out, so a refused row is said by name.
 *
 * Which case of a member's row a press lands on: Opt-In, Opt-Out and Stop act on a TEXT REPLY, so the
 * newest of the row's entries that the press fits (lib/reply-groups opFits, the server's rule mirrored);
 * a row with none is not sent and says why. Send results and Send replacement kit land on the row's
 * newest entry.
 *
 * PART S2 (William, 17 Oct 2026 12:50 AM: "have a button on top that lights up to do a Match Member and then also
 * allow the user to click other quick clicks - so I can both match the member (confirm identity) and the specific
 * task"; 12:53 AM: "And then it would send all for approval"):
 *   - MATCH MEMBER, lit when every ticked row has a suggested member (greyed with the reason on hover otherwise),
 *     queues one match per ticked row (POST /api/care/event/match-member, decision none). The rows stay ticked.
 *   - With a match waiting, OPT-OUT and STOP on a row no text reply fits (a call, an email, an unmatched text) change
 *     that match's decision (amend: opt out of this campaign, or stop), with a reason: the approval shows the match and
 *     the task as one item and applies the task to the matched member. Send results and Send replacement kit on a
 *     text row act on the waiting match's member at approval (the server's rule, care/quick-key.ts). Voice and email
 *     rows keep today's refusals for Opt-In, Send results and Send replacement kit.
 *   - SEND TO APPROVAL QUEUE, lit when every ticked row has something queued, sends those cases to the Approval Queue
 *     (POST /api/care/event/review, verb send; a case a queued task already moved into review counts as sent). Their
 *     rows leave the list (onSentToApproval); a row that failed stays, with its reason.
 *   A row with a match waiting keeps its tick after every press, so the pass ends with Send to Approval Queue.
 *
 * OPT-OUT EVERYWHERE (William, 21 Oct 2026: "I select from the queue screen for the "opt out" and then this member row
 * is removed from the list and moved to the approval"), only while the server says optOutEverywhere
 * (MEMBERCARE_OPTOUT_EVERYWHERE); off, the bar is exactly as above:
 *   - OPT-OUT and STOP send each case they queued to the Approval Queue in the same press (verb send; a case the
 *     press already moved into review counts as sent), so the row leaves the list at once (onSentToApproval) and shows
 *     in Final Review as pending. A case that queued but could not be sent stays, with its reason.
 *   - A row no text reply fits and no match waits on, whose entry is MATCHED to a member (a call or an email asking
 *     out), is pressed from the queue too: one Match Member to that same member with the task as its decision (Stop,
 *     or Opt out of this campaign), with the reason, staged exactly as the Find Member route stages it. An UNMATCHED
 *     row is still refused by name (Match Member first, or open it and use Find Member).
 *
 * ONE CLICK (William, 22 Oct 2026 about 5:30 AM, verbatim): "When I click on item 1 (or others), then I click on Opt-Out
 * - then it should immediately go off from this list and go to approvals. Also, it should be one click: (a) check boxes,
 * (b) click Opt-Out - that is it. It just goes out from there. When approvals confirms, then it should go as per our
 * discussion to opt-out in all relevant db's." And about 5:35 AM: "When you build the one-click opt out also make this
 * applicable to one-click opt-in (same process only we opt-in people in all databases). And, one click STOP, same
 * process only it is not just this campaign but all campaigns with this client."
 *   - With opt-out everywhere on, OPT-OUT and STOP run at the click: no confirm box and no reason panel. A match decision
 *     (a waiting match amended, or a matched call or email pressed as a Match Member) carries an automatic reason
 *     (autoReason), which the server stamps with the operator as for a typed one.
 *   - OPT-IN does the same while its own switch is on (MEMBERCARE_OPTIN_EVERYWHERE, features.optInEverywhere): queued,
 *     then sent to the Approval Queue in the same press. An opt-in acts only on a text reply it fits, never through a
 *     Match Member (the match has no opt-in decision).
 *   - Every ticked row leaves the list at the click, before any request (onLeaving), and its tick clears. When the press
 *     ends every such row is handed back (onReturned): a row sent stays hidden by the page's sent-away rule; a row NOT
 *     sent comes back, ticked again, with its reason in the not-sent lines (QA-1667 F4, QA-1668 FIX 2 as before).
 *   - Off, each press is exactly as above: the confirm box, and the reason panel where it opened.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */

export type QueueAction = 'opt-in' | 'opt-out' | 'stop' | 'resend-results' | 'resend-kit';

/**
 * A ticked row as the bar reads it: a member's row of the grouped view, or (William, 16 Oct 2026 8:20 PM,
 * "Make sure the 'flat events' screen looks the same as the group by member") one entry of the flat view.
 */
export type QueueBarRow = Pick<ThreadRow, 'threadKey' | 'label' | 'eventIds' | 'latestEventId'>;

/**
 * One flat row is one event, so a press lands on that event's own entry: its key is the event id, and its
 * name in a not-sent line is the member cell's first line and the time it came in.
 */
export function eventBarRow(r: EventRow): QueueBarRow {
  return { threadKey: r.eventId, label: `${eventMainLine(r)}, ${fmtTs(r.receivedTs)}`, eventIds: [r.eventId], latestEventId: r.eventId };
}

export type QueueActionTone = 'green' | 'yellow' | 'red' | 'blue' | 'orange';

/**
 * The five buttons, in his order and his words. Each has its own colour (his word, 16 Oct 2026 8:37 PM:
 * "make each button be a different color - Green, yellow, red, blue, orage") and its own icon (8:35 PM
 * "funny icons", the set he settled at 8:40 PM: "Opt in - checkmark; opt out x; stop okay; results- make a
 * paper with writing", the kit's rocket kept from the picture he said yes to). The icons are decoration
 * (aria-hidden): the words stay on every button.
 */
export const QUEUE_ACTIONS: ReadonlyArray<{ op: QueueAction; label: string; icon: string; tone: QueueActionTone }> = [
  { op: 'opt-in', label: 'Opt-In', icon: '✅', tone: 'green' },
  { op: 'opt-out', label: 'Opt-Out', icon: '❌', tone: 'yellow' },
  { op: 'stop', label: 'Stop', icon: '\u{1F6D1}', tone: 'red' },
  { op: 'resend-results', label: 'Send results', icon: '\u{1F4DD}', tone: 'blue' },
  { op: 'resend-kit', label: 'Send replacement kit', icon: '\u{1F680}', tone: 'orange' },
];

const rows = (n: number): string => `${n} ${n === 1 ? 'row' : 'rows'}`;

export const QUEUE_BAR_WORDING = {
  ticked: (n: number) => `${n} ticked`,
  clear: 'Clear ticks',
  replyButtonsOff: 'The reply buttons are switched off on this deployment.',
  masterOff: 'Waits for the master link, which is switched off on this deployment.',
  noTextReply: 'no text reply on this row that the press fits: open the row and record it on the case',
  /** S2: the same, on a row with a suggested member and no match queued yet. */
  matchFirst: 'no text reply on this row that the press fits: press Match Member first, then Opt-Out or Stop',
  /**
   * I1 a (William, 17 Oct 2026): an Opt-Out or a Stop puts its texting block on at once (PROVISIONAL, the server's
   * PART N), so its box says so; every other task changes nothing until approval.
   */
  confirm: (n: number, label: string, op?: QueueAction) => (op === 'opt-out' || op === 'stop'
    ? `Send ${rows(n)} to Final Review as ${label}? ${op === 'stop' ? STOP_WAITS_WORDING.stop : STOP_WAITS_WORDING.optOut}`
    : `Send ${rows(n)} to Final Review as ${label}? Nothing takes effect until a reviewer approves each case.`),
  reasonLabel: 'Reason (required)',
  kitLabel: 'Kit',
  kitFromRecord: "From the member's record",
  send: 'Send to Final Review',
  cancel: 'Cancel',
  /** QA-1647 F1: a request that threw mid-run. The press that threw and every ticked row after it. */
  threw: (why: string) => `not sent, the request failed (${why}); press again to retry`,
  result: (queued: number, notQueued: number, label: string) =>
    `${label}: ${queued} sent to Final Review${notQueued ? `, ${notQueued} not sent` : ''}.`,
  /* PART S2 (William, 17 Oct 2026) */
  matchButton: 'Match Member',
  matchHelp: 'Queue a match to each ticked row\'s suggested member; it is confirmed at approval, never before',
  matchNone: (n: number) => `${n} of the ticked rows ${n === 1 ? 'has' : 'have'} no suggested member: open ${n === 1 ? 'it' : 'them'} and use Find Member.`,
  matchConfirm: (n: number) => `Queue Match Member for ${rows(n)}, each to its suggested member? Nothing changes until a reviewer approves each case.`,
  matchResult: (queued: number, notQueued: number) => `Match Member: ${queued} queued for approval${notQueued ? `, ${notQueued} not queued` : ''}.`,
  noSuggestion: 'no suggested member on this row: open it and use Find Member',
  approvalButton: 'Send to Approval Queue',
  approvalHelp: 'Send the ticked rows, with what is queued on them, to the Approval Queue; their rows leave this list',
  approvalNone: (n: number) => `${n} of the ticked rows ${n === 1 ? 'has' : 'have'} nothing queued: press Match Member or a task first.`,
  approvalConfirm: (n: number) => `Send ${rows(n)} to the Approval Queue? Nothing else takes effect until a reviewer approves each case (a queued Opt-Out or Stop already has its texting block on).`,
  approvalResult: (sent: number, notSent: number) => `Send to Approval Queue: ${sent} sent${notSent ? `, ${notSent} not sent` : ''}.`,
  nothingQueued: 'nothing is queued on this row',
  /* Opt-out everywhere (William, 21 Oct 2026) */
  /** ONE CLICK (William, 22 Oct 2026): the reason a one-click Opt-Out or Stop carries where the server needs one. */
  autoReason: (op: QueueAction) => (op === 'stop' ? 'Stop pressed from the Inbound queue' : 'Opt-Out pressed from the Inbound queue'),
  queuedNotSent: (why: string) => `queued for approval, but not sent to the Approval Queue (${why}); press Send to Approval Queue`,
  /** QA-1667 F4: how many were sent, and by name which rows were not (they stay ticked). */
  everywhereResult: (sent: number, notSent: readonly string[], label: string) =>
    `${label}: ${sent} sent to the Approval Queue${notSent.length ? `, ${notSent.length} not sent (${notSent.join(', ')}); the rows not sent stay ticked` : ''}.`,
} as const;

const isReplyAction = (op: QueueAction): op is 'opt-in' | 'opt-out' | 'stop' => op === 'opt-in' || op === 'opt-out' || op === 'stop';
const needsReason = (op: QueueAction): boolean => op === 'resend-results' || op === 'resend-kit';
/** S2: the reply presses a waiting match can carry as its decision. */
const isMatchDecisionOp = (op: QueueAction): op is 'opt-out' | 'stop' => op === 'opt-out' || op === 'stop';

/**
 * The entry of a member's row a press lands on, or '' when none fits. The row's events are newest first
 * (lib/thread-model), so the first that fits is the newest.
 */
export function entryForAction(op: QueueAction, thread: Pick<ThreadRow, 'eventIds' | 'latestEventId'>, eventsById: ReadonlyMap<string, EventRow>): string {
  if (!isReplyAction(op)) return thread.latestEventId;
  for (const id of thread.eventIds) {
    const row = eventsById.get(id);
    if (row && opFits(op, row)) return id;
  }
  return '';
}

/**
 * Opt-out everywhere: the newest CALL or EMAIL entry of the row that is MATCHED to a member and has no match waiting,
 * or null. A row no text reply fits is pressed through this entry as a Match Member to its own member (see the header).
 * A text entry is never taken here: a text reply the press does not fit was refused by its own rules (a later answer,
 * a note, the class), and this path must not step round them.
 */
export function matchedEntry(row: Pick<QueueBarRow, 'eventIds'>, eventsById: ReadonlyMap<string, EventRow>): { eventId: string; memberId: string; campaign: string | null } | null {
  for (const id of row.eventIds) {
    const r = eventsById.get(id);
    if (r && (r.channel === 'Voice' || r.channel === 'Email') && r.memberId && !r.matchPending) {
      return { eventId: id, memberId: r.memberId, campaign: r.campaign ?? null };
    }
  }
  return null;
}

/** S2: the newest entry of the row with a suggested member (no member yet), or null. */
export function suggestedEntry(row: Pick<QueueBarRow, 'eventIds'>, eventsById: ReadonlyMap<string, EventRow>): { eventId: string; memberId: string; campaign: string | null } | null {
  for (const id of row.eventIds) {
    const r = eventsById.get(id);
    if (r && !r.memberId && r.suggestion?.memberId) return { eventId: id, memberId: r.suggestion.memberId, campaign: r.suggestion.campaign ?? null };
  }
  return null;
}

/** S2: the entry of the row with a Find Member match waiting (on the list, or queued in this pass), or ''. */
export function matchEntry(row: Pick<QueueBarRow, 'eventIds'>, eventsById: ReadonlyMap<string, EventRow>, matchedHere: ReadonlySet<string>): string {
  for (const id of row.eventIds) {
    if (matchedHere.has(id) || eventsById.get(id)?.matchPending) return id;
  }
  return '';
}

/** S2: the row's entries with something queued for approval (a match, a press), on the list or in this pass. */
export function queuedEntries(row: Pick<QueueBarRow, 'eventIds'>, eventsById: ReadonlyMap<string, EventRow>, queuedHere: ReadonlySet<string>): string[] {
  return row.eventIds.filter((id) => {
    const r = eventsById.get(id);
    return queuedHere.has(id) || !!r?.matchPending || (r?.pendingCount ?? 0) > 0;
  });
}

/** Why a button is off right now ('' = it can be pressed), so its tooltip says so. */
export function actionBlock(op: QueueAction, ctx: { ticked: number; receiveOnly: boolean; replyButtons: boolean; masterDecisions: boolean }): string {
  if (ctx.receiveOnly && (op === 'opt-in' || op === 'resend-results' || op === 'resend-kit')) return RECEIVE_ONLY_WORDING.pressRefused;
  if (isReplyAction(op) && !ctx.replyButtons) return QUEUE_BAR_WORDING.replyButtonsOff;
  if (!isReplyAction(op) && !ctx.masterDecisions) return QUEUE_BAR_WORDING.masterOff;
  if (ctx.ticked === 0) return 'Tick one or more rows first.';
  return '';
}

/** S2: why Match Member is off ('' = lit): it lights only when EVERY ticked row has a suggested member. */
export function matchBlock(ticked: ReadonlyArray<QueueBarRow>, eventsById: ReadonlyMap<string, EventRow>): string {
  if (!ticked.length) return 'Tick one or more rows first.';
  const without = ticked.filter((t) => !suggestedEntry(t, eventsById)).length;
  return without ? QUEUE_BAR_WORDING.matchNone(without) : '';
}

/** S2: why Send to Approval Queue is off ('' = lit): it lights only when EVERY ticked row has something queued. */
export function approvalBlock(ticked: ReadonlyArray<QueueBarRow>, eventsById: ReadonlyMap<string, EventRow>, queuedHere: ReadonlySet<string>): string {
  if (!ticked.length) return 'Tick one or more rows first.';
  const without = ticked.filter((t) => !queuedEntries(t, eventsById, queuedHere).length).length;
  return without ? QUEUE_BAR_WORDING.approvalNone(without) : '';
}

export interface QueueActionBarProps {
  /** The ticked rows on screen, in screen order. */
  ticked: ReadonlyArray<QueueBarRow>;
  eventsById: ReadonlyMap<string, EventRow>;
  onClear: () => void;
  /** The sentence to show, the rows sent (their ticks are cleared), and the lines for the rows not sent. */
  onPressed: (sentence: string, sentKeys: string[], notSent: string[]) => void;
  /** S2: the entries Send to Approval Queue sent, so their rows leave the list. */
  onSentToApproval?: (eventIds: string[]) => void;
  /** ONE CLICK (William, 22 Oct 2026): the rows a one-click press takes off the list at the click; clear their ticks. */
  onLeaving?: (rowKeys: string[]) => void;
  /**
   * ONE CLICK: the press ended. Stop hiding `rowKeys` (a row sent stays hidden by the sent-away rule) and tick `tickAgain`
   * again: the rows not sent, which come back with their reason.
   */
  onReturned?: (rowKeys: string[], tickAgain: string[]) => void;
}

type Busy = QueueAction | 'match' | 'approval' | '';

const whyOf = (res: { status: number; data: unknown }): string => {
  const d = (res.data ?? {}) as { message?: unknown; error?: unknown };
  return String(d.message ?? d.error ?? `HTTP ${res.status}`);
};

export function QueueActionBar({ ticked, eventsById, onClear, onPressed, onSentToApproval, onLeaving, onReturned }: QueueActionBarProps) {
  const client = useClient();
  const features = useFeatures();
  const receiveOnly = isReceiveOnly(client);
  const [busy, setBusy] = useState<Busy>('');
  // Console fault 2 (27 Oct 2026): while a press runs, leaving the page asks first.
  useLeaveGuard(busy !== '');
  const [panel, setPanel] = useState<QueueAction | ''>('');
  const [reason, setReason] = useState('');
  const [kitKind, setKitKind] = useState('');
  /** S2: entries a match was queued on in this pass, and entries anything was queued on (before the list catches up). */
  const [matchedHere, setMatchedHere] = useState<ReadonlySet<string>>(() => new Set());
  const [queuedHere, setQueuedHere] = useState<ReadonlySet<string>>(() => new Set());
  const ctx = { ticked: ticked.length, receiveOnly, replyButtons: features.replyButtons, masterDecisions: features.masterDecisions };
  const labelOf = (op: QueueAction) => QUEUE_ACTIONS.find((a) => a.op === op)?.label ?? op;
  const remember = (set: typeof setQueuedHere, ids: string[]) => {
    if (ids.length) set((prev) => new Set([...prev, ...ids]));
  };
  /** S2: a row whose match waits keeps its tick, so the pass ends with Send to Approval Queue. */
  const keepsTick = (t: QueueBarRow, matched: ReadonlySet<string>) => !!matchEntry(t, eventsById, matched);
  /** S2: the rows a reply press would carry as a match decision (no text reply fits, a match waits). */
  const amendRows = (op: QueueAction) => (isMatchDecisionOp(op)
    ? ticked.filter((t) => !entryForAction(op, t, eventsById) && !!matchEntry(t, eventsById, matchedHere))
    : []);
  /** Opt-out everywhere: the rows pressed as a Match Member to their own matched member (no text reply, no match). */
  const everywhere = (op: QueueAction): boolean => features.optOutEverywhere === true && isMatchDecisionOp(op);
  const directRows = (op: QueueAction) => (everywhere(op)
    ? ticked.filter((t) => !entryForAction(op, t, eventsById) && !matchEntry(t, eventsById, matchedHere) && !!matchedEntry(t, eventsById))
    : []);
  /** ONE CLICK OPT-IN (William, 22 Oct 2026 about 5:35 AM): only while the server says optInEverywhere. */
  const optInEverywhere = (op: QueueAction): boolean => features.optInEverywhere === true && op === 'opt-in';
  /** ONE CLICK (William, 22 Oct 2026): the presses that run at the click and send each queued case to the Approval Queue. */
  const oneClick = (op: QueueAction): boolean => everywhere(op) || optInEverywhere(op);

  const run = async (op: QueueAction) => {
    const label = labelOf(op);
    const quick = oneClick(op);
    if (!quick && !window.confirm(QUEUE_BAR_WORDING.confirm(ticked.length, label, op))) return;
    /** ONE CLICK: a match decision's reason is the automatic one (the server needs one for Opt-Out and Stop). */
    const why = everywhere(op) ? QUEUE_BAR_WORDING.autoReason(op) : reason.trim();
    /** ONE CLICK: every ticked row leaves the list now, before any request. */
    const leftKeys = quick ? ticked.map((t) => t.threadKey) : [];
    if (leftKeys.length) onLeaving?.(leftKeys);
    setBusy(op);
    const sentKeys: string[] = [];
    const notSent: string[] = [];
    /** QA-1667 F4: the name of each row not sent (as the bar shows it: the row's label, else the case id). */
    const notSentNames: string[] = [];
    const miss = (name: string, why: string) => {
      notSent.push(`${name}: ${why}`);
      if (!notSentNames.includes(name)) notSentNames.push(name);
    };
    const queuedIds: string[] = [];
    // QA-1647 F1: every ticked row this run has answered for (sent or said not sent), so a request that
    // throws midway can list the press that threw and every row after it, and the queue still reloads.
    const accounted = new Set<string>();
    let queued = 0;
    /** Opt-out everywhere: the row of each queued entry, so a case sent to the Approval Queue can clear its tick. */
    const rowOfQueued = new Map<string, QueueBarRow>();
    /** Opt-out everywhere: the cases this press sent to the Approval Queue (their rows leave the list). */
    const sentAway: string[] = [];
    /** QA-1668 FIX 2: the cases the Approval Queue answered no (already named, with the server's reason). */
    const reviewRefused = new Set<string>();
    /** Console faults 1 and 2: the cases a one-click match request already sent to the Approval Queue itself. */
    const sentInline = new Set<string>();
    const sent = (t: QueueBarRow | undefined, eventId: string) => {
      queued += 1;
      queuedIds.push(eventId);
      if (t) rowOfQueued.set(eventId, t);
      // QA-1667 F4: with opt-out everywhere on, a row clears its tick only once its case reached the Approval Queue
      // (below); a row queued but not sent stays ticked, so Send to Approval Queue can carry it.
      if (t && !quick && !keepsTick(t, matchedHere)) sentKeys.push(t.threadKey);
    };
    try {
      if (isReplyAction(op)) {
        const keyByEvent = new Map<string, QueueBarRow>();
        const amend: Array<{ t: QueueBarRow; eventId: string }> = [];
        const direct: Array<{ t: QueueBarRow; eventId: string; memberId: string; campaign: string | null }> = [];
        for (const t of ticked) {
          const id = entryForAction(op, t, eventsById);
          const waiting = isMatchDecisionOp(op) ? matchEntry(t, eventsById, matchedHere) : '';
          const own = !id && !waiting && everywhere(op) ? matchedEntry(t, eventsById) : null;
          if (id) keyByEvent.set(id, t);
          else if (waiting) amend.push({ t, eventId: waiting });
          else if (own) direct.push({ t, ...own });
          else {
            miss(t.label, isMatchDecisionOp(op) && suggestedEntry(t, eventsById) ? QUEUE_BAR_WORDING.matchFirst : QUEUE_BAR_WORDING.noTextReply);
            accounted.add(t.threadKey);
          }
        }
        for (const ids of inPresses([...keyByEvent.keys()])) {
          const res = await replyActionsToApproval(op, ids, newClientRequestId());
          const rowsBack = res.ok && 'results' in res.data && Array.isArray(res.data.results) ? res.data.results : [];
          if (!rowsBack.length) {
            const why = whyOf(res);
            for (const id of ids) {
              miss(keyByEvent.get(id)?.label ?? id, why);
              const k = keyByEvent.get(id)?.threadKey;
              if (k) accounted.add(k);
            }
            continue;
          }
          for (const row of rowsBack) {
            const t = keyByEvent.get(row.eventId);
            if (t) accounted.add(t.threadKey);
            if (row.status === 'staged') sent(t, row.eventId);
            else miss(t?.label ?? row.eventId, row.reason || row.code || row.status);
          }
        }
        // CONSOLE FAULTS 1 AND 2 (27 Oct 2026): at one click a match decision goes to the Approval Queue in the SAME request
        // (match-member send), so a call or email row is never left in_progress by a send that never ran.
        const matchDone = (t: QueueBarRow, eventId: string, res: { ok: boolean; status: number; data: unknown }) => {
          accounted.add(t.threadKey);
          if (!res.ok) return miss(t.label, whyOf(res));
          sent(t, eventId);
          if (quick && (res.data as { sentToReview?: unknown } | undefined)?.sentToReview === true) sentInline.add(eventId);
        };
        // S2: the waiting match carries the task as its decision (op 'stop' = Stop; Opt-Out = this campaign only).
        for (const { t, eventId } of amend) {
          matchDone(t, eventId, await amendMatchDecision(eventId, op === 'stop' ? 'stop' : 'opt-out-campaign', why, quick));
        }
        // Opt-out everywhere: a matched call or email asking out, as a Match Member to its own member with the task.
        for (const { t, eventId, memberId, campaign } of direct) {
          matchDone(t, eventId, await matchMemberForApproval(eventId, { memberId, campaign, decision: op === 'stop' ? 'stop' : 'opt-out-campaign', reason: why, ...(quick ? { send: true } : {}) }));
        }
        // Opt-out everywhere: every case queued above goes to the Approval Queue now, so its row leaves the list.
        if (quick) {
          const heldKeys = new Set<string>();
          for (const id of queuedIds) {
            const res = sentInline.has(id) ? { ok: true, status: 200, data: {} } : await reviewEvent(id, 'send');
            const already = !res.ok && res.status === 400 && (res.data as { status?: unknown } | undefined)?.status === 'in_review';
            const t = rowOfQueued.get(id);
            if (res.ok || already) {
              sentAway.push(id);
              if (t && !sentKeys.includes(t.threadKey)) sentKeys.push(t.threadKey);
            } else {
              reviewRefused.add(id);
              miss(t?.label ?? id, QUEUE_BAR_WORDING.queuedNotSent(whyOf(res)));
              if (t) heldKeys.add(t.threadKey);
            }
          }
          // QA-1667 F4: a row with any case not sent stays ticked, even when another of its cases was sent.
          for (let i = sentKeys.length - 1; i >= 0; i -= 1) if (heldKeys.has(sentKeys[i]!)) sentKeys.splice(i, 1);
        }
      } else {
        for (const t of ticked) {
          const eventId = entryForAction(op, t, eventsById);
          const res = await quickKey({
            eventId,
            op,
            clientRequestId: newClientRequestId(),
            reason: reason.trim(),
            ...(op === 'resend-kit' && kitKind ? { kitKind } : {}),
            toApproval: true,
          });
          const d = res.data as { status?: unknown; staged?: unknown };
          accounted.add(t.threadKey);
          if (res.ok && (d.staged === true || d.status === 'staged')) sent(t, eventId);
          else miss(t.label, whyOf(res));
        }
      }
    } catch (err) {
      const why = err instanceof Error && err.message ? err.message : String(err || 'no answer');
      for (const t of ticked) {
        if (!accounted.has(t.threadKey)) miss(t.label, QUEUE_BAR_WORDING.threw(why));
      }
      // QA-1668 FIX 2: with opt-out everywhere on, a case queued but never sent to the Approval Queue because a request
      // threw (the send that threw and every one after it) is said NOT SENT by name, and its row stays ticked, even
      // when another case of the same row was sent before the throw. Before, the row was already counted as answered,
      // so the bar said "n sent" and named nothing.
      if (quick) {
        const held = new Set<string>();
        for (const id of queuedIds) {
          if (sentAway.includes(id) || reviewRefused.has(id)) continue;
          const t = rowOfQueued.get(id);
          // Console faults 1 and 2: a case its own match request sent is in the Approval Queue, whatever threw after it.
          if (sentInline.has(id)) {
            sentAway.push(id);
            if (t && !sentKeys.includes(t.threadKey)) sentKeys.push(t.threadKey);
            continue;
          }
          miss(t?.label ?? id, QUEUE_BAR_WORDING.queuedNotSent(why));
          if (t) held.add(t.threadKey);
        }
        for (let i = sentKeys.length - 1; i >= 0; i -= 1) if (held.has(sentKeys[i]!)) sentKeys.splice(i, 1);
      }
    } finally {
      setBusy('');
      setPanel('');
      setReason('');
      setKitKind('');
    }
    remember(setQueuedHere, queuedIds);
    // ONE CLICK: hand back every row taken off at the click; a row not sent comes back ticked, with its reason below.
    if (leftKeys.length) onReturned?.(leftKeys, leftKeys.filter((k) => !sentKeys.includes(k)));
    if (sentAway.length) onSentToApproval?.(sentAway);
    onPressed(quick ? QUEUE_BAR_WORDING.everywhereResult(sentAway.length, notSentNames, label) : QUEUE_BAR_WORDING.result(queued, notSent.length, label), sentKeys, notSent);
  };

  /** S2: Match Member, one match per ticked row, to its suggested member, decision none. The rows stay ticked. */
  const runMatch = async () => {
    if (!window.confirm(QUEUE_BAR_WORDING.matchConfirm(ticked.length))) return;
    setBusy('match');
    const notSent: string[] = [];
    const matched: string[] = [];
    const accounted = new Set<string>();
    try {
      for (const t of ticked) {
        const s = suggestedEntry(t, eventsById);
        if (!s) {
          notSent.push(`${t.label}: ${QUEUE_BAR_WORDING.noSuggestion}`);
          accounted.add(t.threadKey);
          continue;
        }
        const res = await matchMemberForApproval(s.eventId, { memberId: s.memberId, campaign: s.campaign, decision: 'none', reason: '' });
        accounted.add(t.threadKey);
        if (res.ok) matched.push(s.eventId);
        else notSent.push(`${t.label}: ${whyOf(res)}`);
      }
    } catch (err) {
      const why = err instanceof Error && err.message ? err.message : String(err || 'no answer');
      for (const t of ticked) {
        if (!accounted.has(t.threadKey)) notSent.push(`${t.label}: ${QUEUE_BAR_WORDING.threw(why)}`);
      }
    } finally {
      setBusy('');
    }
    remember(setMatchedHere, matched);
    remember(setQueuedHere, matched);
    onPressed(QUEUE_BAR_WORDING.matchResult(matched.length, notSent.length), [], notSent);
  };

  /** S2: Send to Approval Queue, every ticked row's queued entries; sent rows leave the list, a failure stays. */
  const runApproval = async () => {
    if (!window.confirm(QUEUE_BAR_WORDING.approvalConfirm(ticked.length))) return;
    setBusy('approval');
    const sentKeys: string[] = [];
    const sentIds: string[] = [];
    const notSent: string[] = [];
    const accounted = new Set<string>();
    try {
      for (const t of ticked) {
        const ids = queuedEntries(t, eventsById, queuedHere);
        if (!ids.length) {
          notSent.push(`${t.label}: ${QUEUE_BAR_WORDING.nothingQueued}`);
          accounted.add(t.threadKey);
          continue;
        }
        const why: string[] = [];
        const ok: string[] = [];
        for (const id of ids) {
          const res = await reviewEvent(id, 'send');
          // A case a queued task already moved into review is in the Approval Queue: counted as sent.
          const already = !res.ok && res.status === 400 && (res.data as { status?: unknown } | undefined)?.status === 'in_review';
          if (res.ok || already) ok.push(id);
          else why.push(whyOf(res));
        }
        accounted.add(t.threadKey);
        sentIds.push(...ok);
        if (!why.length) sentKeys.push(t.threadKey);
        else notSent.push(`${t.label}: ${why.join('; ')}`);
      }
    } catch (err) {
      const why = err instanceof Error && err.message ? err.message : String(err || 'no answer');
      for (const t of ticked) {
        if (!accounted.has(t.threadKey)) notSent.push(`${t.label}: ${QUEUE_BAR_WORDING.threw(why)}`);
      }
    } finally {
      setBusy('');
    }
    if (sentIds.length) onSentToApproval?.(sentIds);
    onPressed(QUEUE_BAR_WORDING.approvalResult(sentKeys.length, notSent.length), sentKeys, notSent);
  };

  const press = (op: QueueAction) => {
    // ONE CLICK (William, 22 Oct 2026): "(a) check boxes, (b) click Opt-Out - that is it." No panel, no confirm box.
    if (oneClick(op)) {
      void run(op);
      return;
    }
    if (needsReason(op) || amendRows(op).length > 0 || directRows(op).length > 0) {
      setPanel((cur) => (cur === op ? '' : op));
      return;
    }
    void run(op);
  };

  const reasonOk = reason.trim().length > 0 && reason.trim().length <= MAX_REASON;
  const mBlock = matchBlock(ticked, eventsById);
  const aBlock = approvalBlock(ticked, eventsById, queuedHere);

  return (
    <div className="queue-bar" role="toolbar" aria-label="Actions for the ticked rows" data-testid="queue-action-bar">
      <span className="queue-bar-count" data-testid="queue-bar-count">{QUEUE_BAR_WORDING.ticked(ticked.length)}</span>
      <button
        type="button"
        className="btn btn-sm btn-teal"
        disabled={busy !== '' || !!mBlock}
        title={mBlock || QUEUE_BAR_WORDING.matchHelp}
        onClick={() => void runMatch()}
        data-testid="queue-match-member"
      >
        {busy === 'match' ? `${QUEUE_BAR_WORDING.matchButton}...` : QUEUE_BAR_WORDING.matchButton}
      </button>
      {QUEUE_ACTIONS.map((a) => {
        const block = actionBlock(a.op, ctx);
        return (
          <button
            key={a.op}
            type="button"
            className={`btn btn-sm qbtn qbtn-${a.tone}${panel === a.op ? ' qbtn-open' : ''}`}
            disabled={busy !== '' || !!block}
            title={block || `Send the ticked rows to Final Review as ${a.label}`}
            onClick={() => press(a.op)}
            data-testid={`queue-action-${a.op}`}
          >
            <span className="qbtn-icon" aria-hidden="true" data-testid={`queue-action-icon-${a.op}`}>{a.icon}</span>
            {busy === a.op ? `${a.label}...` : a.label}
          </button>
        );
      })}
      <button
        type="button"
        className="btn btn-sm btn-primary"
        disabled={busy !== '' || !!aBlock}
        title={aBlock || QUEUE_BAR_WORDING.approvalHelp}
        onClick={() => void runApproval()}
        data-testid="queue-send-approval"
      >
        {busy === 'approval' ? `${QUEUE_BAR_WORDING.approvalButton}...` : QUEUE_BAR_WORDING.approvalButton}
      </button>
      {ticked.length > 0 && (
        <button type="button" className="btn btn-sm btn-ghost" onClick={onClear} disabled={busy !== ''}>
          {QUEUE_BAR_WORDING.clear}
        </button>
      )}
      {panel && (
        <div className="queue-bar-panel" data-testid="queue-bar-panel">
          <label>
            <span className="micro muted">{QUEUE_BAR_WORDING.reasonLabel}</span>
            <input
              className="text-input"
              aria-label={QUEUE_BAR_WORDING.reasonLabel}
              value={reason}
              maxLength={MAX_REASON}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          {panel === 'resend-kit' && (
            <label>
              <span className="micro muted">{QUEUE_BAR_WORDING.kitLabel}</span>
              <select aria-label={QUEUE_BAR_WORDING.kitLabel} value={kitKind} onChange={(e) => setKitKind(e.target.value)}>
                <option value="">{QUEUE_BAR_WORDING.kitFromRecord}</option>
                {KIT_KINDS.map((k) => (
                  <option key={k} value={k}>{k.toUpperCase()}</option>
                ))}
              </select>
            </label>
          )}
          <button type="button" className="btn btn-sm btn-teal" disabled={!reasonOk || busy !== ''} onClick={() => void run(panel)}>
            {QUEUE_BAR_WORDING.send}
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPanel('')}>
            {QUEUE_BAR_WORDING.cancel}
          </button>
        </div>
      )}
    </div>
  );
}
